import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, Mail, Lock, Eye, EyeOff, Briefcase, Zap, TrendingUp, Users, Award, Star, Download, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import DeviceVerificationModal from '@/components/DeviceVerificationModal';
import supabase from '@/lib/customSupabaseClient';

const LoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, user, loading, securityStatus } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showDeviceVerification, setShowDeviceVerification] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);

  useEffect(() => {
    if (user && !loading) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: '⚠️ Missing Information', description: 'Please enter both email and password to continue.', variant: 'destructive' });
      return;
    }
    if (attemptCount >= 5) {
      toast({ title: '🔒 Too Many Attempts', description: 'Please wait 5 minutes before trying again for security reasons.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    setAttemptCount(prev => prev + 1);
    try {
      const { error } = await signIn(email, password);
      if (error) throw error;
      toast({ title: '🎉 Welcome Back!', description: 'Successfully signed in! Redirecting to your dashboard...', variant: 'success' });
      if (securityStatus.requiresDeviceVerification) {
        setShowDeviceVerification(true);
      } else {
        setTimeout(() => { navigate('/'); }, 1000);
      }
    } catch (error) {
      let errorMessage = 'Invalid credentials. Please try again.';
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'The email or password you entered is incorrect. Please check your credentials and try again.';
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = 'Please check your email and confirm your account before logging in.';
      } else if (error.message.includes('Too many requests')) {
        errorMessage = 'Too many login attempts. Please wait a moment before trying again.';
      }
      toast({ title: '❌ Login Failed', description: errorMessage, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast({ title: '⚠️ Email Required', description: 'Please enter your email address.', variant: 'destructive' });
      return;
    }
    setIsSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: 'https://tradetitans.pro/reset-password',
      });
      if (error) throw error;
      toast({ title: '📧 Reset Link Sent', description: 'Check your email for a password reset link. It may take a few minutes to arrive.', variant: 'success' });
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast({ title: '❌ Failed to Send', description: 'Could not send reset email. Please try again.', variant: 'destructive' });
    } finally {
      setIsSendingReset(false);
    }
  };

  const finalLoadingState = isSubmitting || loading;

  const testimonials = [
    { text: "Increased our efficiency by 300%", author: "Mike Johnson, Electrician" },
    { text: "Best field service app I've ever used", author: "Sarah Chen, HVAC Tech" },
    { text: "Saves me 2 hours every day", author: "David Rodriguez, Plumber" }
  ];

  const features = [
    { icon: Zap, title: "Lightning Fast", description: "Real-time updates and instant notifications" },
    { icon: TrendingUp, title: "Smart Analytics", description: "Track performance and optimize operations" },
    { icon: Users, title: "Team Collaboration", description: "Seamless communication with your crew" },
    { icon: Award, title: "Award Winning", description: "Trusted by 10,000+ field professionals" }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="flex items-center justify-center">
          <Card className="w-full max-w-md bg-slate-800/90 backdrop-blur-lg border-slate-700 shadow-2xl shadow-purple-500/20">
            <CardHeader className="text-center">
              <motion.div className="mx-auto w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-4 shadow-lg" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Briefcase className="w-8 h-8 text-white" />
              </motion.div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Welcome to TradeTitans</CardTitle>
              <CardDescription className="text-slate-400">Sign in to manage your field operations like a pro</CardDescription>
            </CardHeader>
            <CardContent>
              <AnimatePresence mode="wait">
                {!showForgotPassword ? (
                  <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <form onSubmit={handleLogin} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="email" type="email" placeholder="you@yourcompany.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={finalLoadingState} className="pl-10 bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-slate-300">Password</Label>
                          <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-purple-400 hover:text-purple-300 hover:underline transition-colors">
                            Forgot Password?
                          </button>
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={finalLoadingState} className="pl-10 pr-10 bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors" aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <AnimatePresence>
                        {attemptCount > 0 && attemptCount < 5 && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="text-xs text-orange-400 bg-orange-900/20 p-2 rounded">
                            Login attempt {attemptCount} of 5
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 text-base font-semibold transition-all duration-200 transform hover:scale-105" disabled={finalLoadingState}>
                        {finalLoadingState ? (<motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />) : (<LogIn className="w-5 h-5 mr-2" />)}
                        {finalLoadingState ? 'Signing In...' : 'Sign In'}
                      </Button>
                    </form>
                    <div className="mt-6 text-center space-y-2">
                      <p className="text-sm text-slate-400">Don't have an account?{' '}<Link to="/signup" className="font-medium text-purple-400 hover:underline hover:text-purple-300 transition-colors">Sign up</Link></p>
                      <p className="text-sm text-slate-400">Are you an admin?{' '}<Link to="/admin/login" className="font-medium text-red-400 hover:underline hover:text-red-300 transition-colors">Admin Login</Link></p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <form onSubmit={handleForgotPassword} className="space-y-6">
                      <div className="text-center mb-2">
                        <h3 className="text-lg font-semibold text-white">Reset Your Password</h3>
                        <p className="text-sm text-slate-400 mt-1">Enter your email and we'll send you a reset link.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email" className="text-slate-300">Email Address</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="forgot-email" type="email" placeholder="you@yourcompany.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required disabled={isSendingReset} className="pl-10 bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 text-base font-semibold transition-all duration-200 transform hover:scale-105" disabled={isSendingReset}>
                        {isSendingReset ? (<motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />) : (<Mail className="w-5 h-5 mr-2" />)}
                        {isSendingReset ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </form>
                    <div className="mt-4 text-center">
                      <button type="button" onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }} className="text-sm text-slate-400 hover:text-slate-200 transition-colors">
                        ← Back to Sign In
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex items-center justify-center">
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-white mb-4">Join 10,000+ Field Professionals</h2>
              <p className="text-xl text-slate-300 mb-8">Transform your field operations with the most powerful field service management platform</p>
            </div>
            <div className="space-y-4">
              {testimonials.map((testimonial, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }} className="bg-slate-800/50 backdrop-blur-lg border border-slate-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => (<Star key={i} className="w-4 h-4 fill-current" />))}</div>
                    <div className="flex-1"><p className="text-slate-300 text-sm italic">"{testimonial.text}"</p><p className="text-slate-400 text-xs mt-1">— {testimonial.author}</p></div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }} className="bg-slate-800/50 backdrop-blur-lg border border-slate-700 rounded-lg p-4 hover:bg-slate-800/70 transition-all duration-200">
                  <feature.icon className="w-6 h-6 text-purple-400 mb-2" />
                  <h3 className="text-sm font-semibold text-white mb-1">{feature.title}</h3>
                  <p className="text-slate-400 text-xs">{feature.description}</p>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.0 }} className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg p-6 text-center">
              <div className="flex items-center justify-center mb-3"><Download className="w-6 h-6 text-purple-400 mr-2" /><h3 className="text-xl font-bold text-white">🚀 Get the Mobile App</h3></div>
              <p className="text-slate-300 mb-4 text-sm">Take your field operations on the go with our native mobile app</p>
              <div className="flex justify-center space-x-3">
                <Button size="sm" className="bg-black hover:bg-gray-800 text-white text-xs">📱 App Store</Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs">🤖 Google Play</Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">⭐ 4.8/5 stars from 2,500+ reviews</p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <DeviceVerificationModal isOpen={showDeviceVerification} onClose={() => setShowDeviceVerification(false)} onVerificationComplete={() => { setShowDeviceVerification(false); navigate('/'); }} />

      {securityStatus.blockedDevices && securityStatus.blockedDevices.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-sm">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-orange-900">Device Limit Reached</h4>
              <p className="text-sm text-orange-700 mt-1">You have reached the maximum number of devices for your plan. Please log out from another device first.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
